<template>
    <teleport v-if="visible" to="#root-fullscreen">
        <div data-scroll-lock class="pointer-events-auto fixed inset-0 z-80" role="presentation">
            <div v-if="anchorRect" class="interactive-tutorial__spotlight" :style="spotlightStyle"></div>
            <div v-else class="absolute inset-0 bg-black/80"></div>

            <section
                ref="dialogRef"
                class="bg-dark-base border-dark-lightest fixed z-10 max-h-[calc(var(--app-viewport-height)-2rem)] w-[calc(100%-2rem)] max-w-md overflow-y-auto rounded-2xl border p-5 shadow-2xl"
                :style="cardStyle"
                role="dialog"
                aria-modal="true"
                :aria-labelledby="`interactive-tutorial-${stage}-title`"
                tabindex="-1"
                @keydown.tab="trapFocus"
            >
                <div class="text-brand-base mb-2 text-xs font-semibold tracking-wide uppercase">
                    {{ stepLabel }}
                </div>
                <h2 :id="`interactive-tutorial-${stage}-title`" class="mb-3 text-xl font-bold">
                    {{ $t(content.title) }}
                </h2>

                <div class="text-text-base/85 space-y-2 text-sm leading-6">
                    <ol v-if="content.steps" class="space-y-2">
                        <li v-for="(step, index) in content.steps" :key="step" class="flex gap-2">
                            <span class="text-brand-base font-semibold">{{ index + 1 }}.</span>
                            <span>{{ $t(step) }}</span>
                        </li>
                    </ol>
                    <p v-for="paragraph in content.paragraphs" :key="paragraph">
                        {{ $t(paragraph) }}
                    </p>
                </div>

                <div v-if="isWebHandoff" class="mt-5 flex flex-wrap items-center justify-center gap-3">
                    <a v-if="appStoreUrl" :href="appStoreUrl" target="_blank" rel="noopener noreferrer">
                        <img :src="appleBadge" :alt="$t('interactive-tutorial.web.apple-alt')" class="h-12" />
                    </a>
                    <a v-if="playStoreUrl" :href="playStoreUrl" target="_blank" rel="noopener noreferrer">
                        <img :src="googleBadge" :alt="$t('interactive-tutorial.web.google-alt')" class="h-12" />
                    </a>
                </div>

                <p v-if="saveError" class="text-brand-red mt-3 text-sm" role="alert">
                    {{ $t('interactive-tutorial.save-error') }}
                </p>

                <div v-if="!isWebHandoff" class="mt-5 flex flex-col gap-3">
                    <Button
                        :action="runPrimaryAction"
                        :enabled="!saving"
                        role="button"
                        tabindex="0"
                        :aria-disabled="saving"
                        @keydown.enter.prevent="runPrimaryAction"
                        @keydown.space.prevent="runPrimaryAction"
                    >
                        {{ $t(content.primary) }}
                    </Button>
                    <Button
                        v-if="content.secondary && showSecondaryAction"
                        secondary
                        :action="runSecondaryAction"
                        :enabled="!saving"
                        role="button"
                        tabindex="0"
                        :aria-disabled="saving"
                        @keydown.enter.prevent="runSecondaryAction"
                        @keydown.space.prevent="runSecondaryAction"
                    >
                        {{ $t(content.secondary) }}
                    </Button>
                </div>

                <button
                    type="button"
                    class="text-text-darker hover:text-text-base mt-5 w-full text-center text-sm underline underline-offset-4"
                    :disabled="saving"
                    @click="quitTutorial"
                >
                    {{ $t('interactive-tutorial.quit') }}
                </button>
            </section>
        </div>
    </teleport>
</template>

<script setup>
import { computed, inject, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'
import { useI18n } from 'vue-i18n'
import axios from '@/lib/API/client'
import useInteractiveTutorial, {
    openAwaitingValidationSession,
    openPendingMoveProTutorialSession,
} from '@/lib/hooks/use-interactive-tutorial'
import {
    INTERACTIVE_TUTORIAL_STAGES,
    INTERACTIVE_TUTORIAL_STEPS,
    INTERACTIVE_TUTORIAL_VALIDATION_STEP,
} from '@/lib/interactive-tutorial/stages'
import { activeHorsesOf } from '@/lib/horses/archived'
import Button from '@/components/buttons/Primary.vue'

const route = useRoute()
const router = useRouter()
const store = useStore()
const { locale, t } = useI18n()
const isBuildForMobile = inject('isBuildForMobile', false)
const movepro = inject('movepro', { current: { isBusy: false } })
const {
    stage,
    active,
    stepIndex,
    stepCount,
    saving,
    saveError,
    pendingMoveProSessionId,
    awaitingValidationSessionId,
    start,
    useExistingHorse,
    quit,
    complete,
} = useInteractiveTutorial()

const anchorRect = ref(null)
const applicationModalOpen = ref(false)
const validationModalOpen = ref(false)
const resultSessionEligibility = ref(null)
const resultSessionId = ref(null)
const hasExistingHorse = ref(false)
const dialogRef = ref(null)
const snoozedView = ref(null)
let mutationObserver = null
let presentationFrame = null
let stopActiveWatcher = null
let horseAvailabilityRequestId = 0
let scrolledViewKey = null

const routeByStage = {
    [INTERACTIVE_TUTORIAL_STAGES.HORSE]: 'horses.index',
    [INTERACTIVE_TUTORIAL_STAGES.RECORD]: 'record.index',
    [INTERACTIVE_TUTORIAL_STAGES.SYNC]: 'sensors.index',
    [INTERACTIVE_TUTORIAL_STAGES.RESULTS]: 'sessions.session',
}

const anchorByStage = {
    [INTERACTIVE_TUTORIAL_STAGES.HORSE]: '[data-interactive-tutorial="horse-add"]',
    [INTERACTIVE_TUTORIAL_STAGES.RECORD]: '[data-interactive-tutorial="movepro-record"]',
    [INTERACTIVE_TUTORIAL_STAGES.SYNC]: '[data-interactive-tutorial="movepro-sync"]',
    [INTERACTIVE_TUTORIAL_STAGES.RESULTS]: '[data-interactive-tutorial="session-results"]',
}

const validationAnchor = '[data-interactive-tutorial="session-validation"]'

const contentByStage = {
    [INTERACTIVE_TUTORIAL_STAGES.INTRO]: {
        title: 'interactive-tutorial.intro.title',
        paragraphs: ['interactive-tutorial.intro.text'],
        primary: 'interactive-tutorial.intro.cta',
    },
    [INTERACTIVE_TUTORIAL_STAGES.HORSE]: {
        title: 'interactive-tutorial.horse.title',
        paragraphs: ['interactive-tutorial.horse.text', 'interactive-tutorial.horse.height'],
        primary: 'interactive-tutorial.horse.cta',
        secondary: 'interactive-tutorial.horse.existing',
    },
    [INTERACTIVE_TUTORIAL_STAGES.RECORD]: {
        title: 'interactive-tutorial.record.title',
        // A real sequence: the card numbers it.
        steps: [
            'interactive-tutorial.record.placement',
            'interactive-tutorial.record.gps',
            'interactive-tutorial.record.orientation',
            'interactive-tutorial.record.start',
        ],
        paragraphs: [
            'interactive-tutorial.record.calibration',
            'interactive-tutorial.record.autonomous',
            'interactive-tutorial.record.polar',
        ],
        primary: 'interactive-tutorial.record.cta',
        secondary: 'interactive-tutorial.later',
    },
    [INTERACTIVE_TUTORIAL_STAGES.SYNC]: {
        title: 'interactive-tutorial.sync.title',
        paragraphs: ['interactive-tutorial.sync.text'],
        primary: 'interactive-tutorial.sync.cta',
        secondary: 'interactive-tutorial.later',
    },
    [INTERACTIVE_TUTORIAL_STAGES.RESULTS]: {
        title: 'interactive-tutorial.results.title',
        paragraphs: [
            'interactive-tutorial.results.overview',
            'interactive-tutorial.results.similarity',
            'interactive-tutorial.results.gaits',
        ],
        primary: 'interactive-tutorial.results.cta',
    },
}

const validationContent = {
    title: 'interactive-tutorial.validation.title',
    paragraphs: ['interactive-tutorial.validation.text'],
    primary: 'interactive-tutorial.validation.cta',
}

const webContent = {
    title: 'interactive-tutorial.web.title',
    paragraphs: ['interactive-tutorial.web.text', 'interactive-tutorial.web.usb', 'interactive-tutorial.web.resume'],
}

const resumeContent = {
    title: 'interactive-tutorial.resume.title',
    primary: 'interactive-tutorial.resume.cta',
}

// The resume card names the step it brings back.
const resumeNextByStage = {
    [INTERACTIVE_TUTORIAL_STAGES.HORSE]: 'interactive-tutorial.resume.horse',
    [INTERACTIVE_TUTORIAL_STAGES.RECORD]: 'interactive-tutorial.resume.record',
    [INTERACTIVE_TUTORIAL_STAGES.SYNC]: 'interactive-tutorial.resume.sync',
    [INTERACTIVE_TUTORIAL_STAGES.RESULTS]: 'interactive-tutorial.resume.results',
}

const isWebHandoff = computed(() => active.value && !isBuildForMobile)
const viewKey = computed(() => `${stage.value}:${String(route.name || '')}`)
const isExpectedRoute = computed(
    () => stage.value === INTERACTIVE_TUTORIAL_STAGES.INTRO || route.name === routeByStage[stage.value]
)
const resultSessionMatchesPending = computed(
    () => !pendingMoveProSessionId.value || String(pendingMoveProSessionId.value) === String(route.params.sessionId)
)
const resultSessionIsEligible = computed(
    () =>
        stage.value === INTERACTIVE_TUTORIAL_STAGES.RESULTS &&
        resultSessionEligibility.value === 'eligible' &&
        String(resultSessionId.value) === String(route.params.sessionId) &&
        resultSessionMatchesPending.value
)
const waitingForResultSession = computed(
    () =>
        stage.value === INTERACTIVE_TUTORIAL_STAGES.RESULTS &&
        isExpectedRoute.value &&
        (resultSessionEligibility.value === null || String(resultSessionId.value) !== String(route.params.sessionId))
)
const tutorialWorkInProgress = computed(
    () =>
        route.name === 'horses.create' ||
        ['record.live', 'record.simple'].includes(route.name) ||
        (stage.value === INTERACTIVE_TUTORIAL_STAGES.SYNC && route.name === 'sessions.session')
)
// The session is synced but not validated yet: the next step is the session page.
const awaitingValidation = computed(
    () => stage.value === INTERACTIVE_TUTORIAL_STAGES.SYNC && Boolean(awaitingValidationSessionId.value)
)
const shouldResume = computed(
    () =>
        !isExpectedRoute.value ||
        awaitingValidation.value ||
        (stage.value === INTERACTIVE_TUTORIAL_STAGES.RESULTS &&
            String(resultSessionId.value) === String(route.params.sessionId) &&
            (resultSessionEligibility.value === 'ineligible' || !resultSessionMatchesPending.value))
)
// After the sync the session page asks to confirm the session: that window gets
// its own card, over it, while the account is still at the sync stage.
const showsValidation = computed(
    () =>
        isBuildForMobile &&
        stage.value === INTERACTIVE_TUTORIAL_STAGES.SYNC &&
        route.name === 'sessions.session' &&
        validationModalOpen.value
)
const content = computed(() => {
    if (isWebHandoff.value) return webContent
    if (showsValidation.value) return validationContent
    if (shouldResume.value) {
        const next = awaitingValidation.value
            ? 'interactive-tutorial.resume.validation'
            : resumeNextByStage[stage.value]
        const resume = { ...resumeContent, paragraphs: [next] }
        if (stage.value !== INTERACTIVE_TUTORIAL_STAGES.SYNC) return resume
        // On the sensors page itself the rider may have other sessions to sync:
        // the card then steps aside there instead of leaving the page.
        return {
            ...resume,
            secondary:
                awaitingValidation.value && route.name === 'sensors.index'
                    ? 'interactive-tutorial.later'
                    : 'interactive-tutorial.resume.sessions',
        }
    }
    return contentByStage[stage.value]
})
const syncIsBusy = computed(() => {
    if (stage.value !== INTERACTIVE_TUTORIAL_STAGES.SYNC) return false

    const files = movepro?.current?.files || []
    return (
        Boolean(movepro?.current?.isBusy) ||
        files.some((file) => file?.properties?.uploading === true) ||
        (Boolean(pendingMoveProSessionId.value) && files.some((file) => file?.properties?.selected === true))
    )
})
const visible = computed(() => {
    if (!active.value || !content.value || saving.value) return false
    if (showsValidation.value) return snoozedView.value !== viewKey.value
    if (applicationModalOpen.value || tutorialWorkInProgress.value) return false
    if (isWebHandoff.value) return true
    if (waitingForResultSession.value || syncIsBusy.value || snoozedView.value === viewKey.value) return false
    return true
})
const showSecondaryAction = computed(() => stage.value !== INTERACTIVE_TUTORIAL_STAGES.HORSE || hasExistingHorse.value)
const displayedStepIndex = computed(() =>
    showsValidation.value || awaitingValidation.value
        ? INTERACTIVE_TUTORIAL_STEPS.indexOf(INTERACTIVE_TUTORIAL_VALIDATION_STEP)
        : stepIndex.value
)
const stepLabel = computed(() =>
    isWebHandoff.value
        ? t('interactive-tutorial.label')
        : t('interactive-tutorial.progress', { current: displayedStepIndex.value + 1, total: stepCount })
)

const supportedLanguages = ['fr', 'en', 'de', 'it']
const language = computed(() => {
    const candidate = String(locale.value || 'en')
        .split('-')[0]
        .toLowerCase()
    return supportedLanguages.includes(candidate) ? candidate : 'en'
})
const appleBadge = computed(() => `/images/badges/apple-store-${language.value}-black.svg`)
const googleBadge = computed(() => `/images/badges/google-play-${language.value}-black.png`)
const playStoreUrl = import.meta.env.VITE_APP_PLAYSTORE_URL
const appStoreUrl = import.meta.env.VITE_APP_APPSTORE_URL

const spotlightStyle = computed(() => {
    if (!anchorRect.value) return {}
    return {
        top: `${anchorRect.value.top - 8}px`,
        left: `${anchorRect.value.left - 8}px`,
        width: `${anchorRect.value.width + 16}px`,
        height: `${anchorRect.value.height + 16}px`,
    }
})

const cardStyle = computed(() => {
    const centered = isWebHandoff.value || !anchorRect.value
    if (centered) {
        return { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }
    }

    const anchorInBottomHalf = anchorRect.value.top + anchorRect.value.height / 2 > window.innerHeight / 2
    return {
        left: '50%',
        transform: 'translateX(-50%)',
        ...(anchorInBottomHalf
            ? { top: 'max(1rem, var(--app-safe-area-top))' }
            : { bottom: 'max(1rem, var(--app-safe-area-bottom))' }),
    }
})

const updateAnchorRect = () => {
    const selector = showsValidation.value ? validationAnchor : anchorByStage[stage.value]
    if (!selector || isWebHandoff.value || (!showsValidation.value && (!isExpectedRoute.value || shouldResume.value))) {
        anchorRect.value = null
        return
    }

    const element = document.querySelector(selector)
    let rect = element?.getBoundingClientRect()
    // Bring an off-screen anchor into view once per screen, and only while the
    // card shows: the rider must stay free to scroll once it is dismissed.
    if (
        visible.value &&
        scrolledViewKey !== viewKey.value &&
        rect?.height &&
        (rect.bottom < 0 || rect.top > window.innerHeight)
    ) {
        scrolledViewKey = viewKey.value
        element.scrollIntoView({ block: 'center' })
        rect = element.getBoundingClientRect()
    }
    anchorRect.value =
        rect?.width && rect?.height ? { top: rect.top, left: rect.left, width: rect.width, height: rect.height } : null
}

const updatePresentationState = () => {
    const openModals = document.querySelectorAll('[data-app-modal-open]')
    applicationModalOpen.value = openModals.length > 0
    // Windows open in order and the last one is on top: the card only covers
    // the confirmation when nothing is stacked on it.
    validationModalOpen.value = Boolean(openModals[openModals.length - 1]?.querySelector(validationAnchor))
    const resultSessionElement = document.querySelector('[data-interactive-tutorial-session]')
    resultSessionEligibility.value = resultSessionElement?.dataset?.interactiveTutorialSession ?? null
    resultSessionId.value = resultSessionElement?.dataset?.interactiveTutorialSessionId ?? null
    updateAnchorRect()
}

const schedulePresentationUpdate = () => {
    if (presentationFrame !== null) return

    presentationFrame = window.requestAnimationFrame(() => {
        presentationFrame = null
        updatePresentationState()
    })
}

const startPresentationTracking = () => {
    if (mutationObserver) return

    updatePresentationState()
    window.addEventListener('resize', schedulePresentationUpdate)
    window.addEventListener('scroll', schedulePresentationUpdate, true)
    mutationObserver = new MutationObserver(schedulePresentationUpdate)
    mutationObserver.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: [
            'data-app-modal-open',
            'data-interactive-tutorial',
            'data-interactive-tutorial-session',
            'data-interactive-tutorial-session-id',
        ],
    })
}

const stopPresentationTracking = () => {
    window.removeEventListener('resize', schedulePresentationUpdate)
    window.removeEventListener('scroll', schedulePresentationUpdate, true)
    mutationObserver?.disconnect()
    mutationObserver = null

    if (presentationFrame !== null) {
        window.cancelAnimationFrame(presentationFrame)
        presentationFrame = null
    }

    anchorRect.value = null
    applicationModalOpen.value = false
    validationModalOpen.value = false
    resultSessionEligibility.value = null
    resultSessionId.value = null
}

const refreshHorseAvailability = async () => {
    const requestId = ++horseAvailabilityRequestId
    const userId = store.state.auth?.user?.id
    hasExistingHorse.value = false

    if (!active.value || isWebHandoff.value || stage.value !== INTERACTIVE_TUTORIAL_STAGES.HORSE || !userId) {
        return
    }

    try {
        const response = await axios.get('/user/horses?all=1')
        const responseData = response?.data
        const horses = Array.isArray(responseData)
            ? responseData
            : Array.isArray(responseData?.data)
              ? responseData.data
              : []

        if (
            requestId === horseAvailabilityRequestId &&
            String(store.state.auth?.user?.id) === String(userId) &&
            stage.value === INTERACTIVE_TUTORIAL_STAGES.HORSE
        ) {
            // A user whose horses are all archived is asked to add one.
            hasExistingHorse.value = activeHorsesOf(horses).length > 0
        }
    } catch {
        // Creating a horse remains available if this optional check fails.
    }
}

const snooze = () => {
    snoozedView.value = viewKey.value
}

const runPrimaryAction = async () => {
    if (saving.value) return

    if (showsValidation.value) {
        snooze()
        return
    }

    if (shouldResume.value) {
        if (stage.value === INTERACTIVE_TUTORIAL_STAGES.SYNC) {
            const opened = (await openPendingMoveProTutorialSession()) || (await openAwaitingValidationSession())
            if (!opened) await router.push({ name: 'sensors.index' })
            return
        }

        if (stage.value === INTERACTIVE_TUTORIAL_STAGES.RESULTS) {
            const openedPendingSession = await openPendingMoveProTutorialSession()
            if (!openedPendingSession) {
                await router.push({ name: 'sessions.index' })
                snooze()
            }
            return
        }

        const routeName = routeByStage[stage.value]
        if (routeName) await router.push({ name: routeName })
        return
    }

    if (stage.value === INTERACTIVE_TUTORIAL_STAGES.INTRO) {
        await start()
        await router.push({ name: 'horses.index' })
        return
    }
    if (stage.value === INTERACTIVE_TUTORIAL_STAGES.HORSE) {
        await router.push({ name: 'horses.create' })
        return
    }
    if ([INTERACTIVE_TUTORIAL_STAGES.RECORD, INTERACTIVE_TUTORIAL_STAGES.SYNC].includes(stage.value)) {
        snooze()
        return
    }
    if (stage.value === INTERACTIVE_TUTORIAL_STAGES.RESULTS && resultSessionIsEligible.value) {
        await complete()
    }
}

const runSecondaryAction = async () => {
    if (saving.value) return

    if (stage.value === INTERACTIVE_TUTORIAL_STAGES.HORSE) {
        await useExistingHorse()
        await router.push({ name: 'record.index' })
        return
    }
    if (stage.value === INTERACTIVE_TUTORIAL_STAGES.SYNC && shouldResume.value) {
        if (content.value?.secondary === 'interactive-tutorial.resume.sessions' && route.name !== 'sessions.index') {
            await router.push({ name: 'sessions.index' })
        }
        snooze()
        return
    }
    snooze()
}

const quitTutorial = async () => {
    await quit()
}

const trapFocus = (event) => {
    const focusable = Array.from(
        dialogRef.value?.querySelectorAll('a[href], button:not([disabled]), [tabindex="0"]') ?? []
    )
    if (!focusable.length) {
        event.preventDefault()
        dialogRef.value?.focus()
        return
    }

    const first = focusable[0]
    const last = focusable[focusable.length - 1]
    if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
    } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
    }
}

watch([stage, () => route.fullPath, isWebHandoff], async () => {
    snoozedView.value = null
    await nextTick()
    if (active.value) schedulePresentationUpdate()
})

watch([stage, () => store.state.auth?.user?.id, isWebHandoff], refreshHorseAvailability, { immediate: true })

// The confirmation slides in for 300 ms: measure it again once it is in place.
watch(showsValidation, (isShown) => {
    if (isShown) window.setTimeout(schedulePresentationUpdate, 350)
})

watch(visible, async (isVisible) => {
    if (!isVisible) return
    await nextTick()
    dialogRef.value?.focus()
})

onMounted(() => {
    stopActiveWatcher = watch(
        active,
        (isActive) => {
            if (isActive) startPresentationTracking()
            else stopPresentationTracking()
        },
        { immediate: true }
    )
})

onBeforeUnmount(() => {
    stopActiveWatcher?.()
    stopPresentationTracking()
})
</script>

<style scoped>
.interactive-tutorial__spotlight {
    position: fixed;
    z-index: 1;
    pointer-events: none;
    border: 2px solid var(--color-brand-base);
    border-radius: 0.875rem;
    box-shadow: 0 0 0 9999px rgb(0 0 0 / 80%);
    transition:
        inset 180ms ease,
        width 180ms ease,
        height 180ms ease;
}
</style>
